import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SharedRoutinePreview } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface RoutinePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  sharedRoutine: SharedRoutinePreview | null;
  onAccept: () => void;
  onDecline: () => void;
  loading: boolean;
}

const RoutinePreviewDialog: React.FC<RoutinePreviewDialogProps> = ({
  open,
  onClose,
  sharedRoutine,
  onAccept,
  onDecline,
  loading,
}) => {
  const { t } = useLanguage();

  if (!sharedRoutine) return null;

  const { routine, shared_by_user, message, created_at } = sharedRoutine;

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalExercises = (): number => {
    return routine.days.reduce((total, day) => total + day.exercises.length, 0);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FitnessCenterIcon />
          {t('routines.previewRoutine')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Routine Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {routine.name}
          </Typography>
          
          {routine.description && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {routine.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {routine.days.length} {t('routines.days')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FitnessCenterIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {getTotalExercises()} {t('routines.exercises')}
              </Typography>
            </Box>
            <Chip 
              label={routine.is_active ? t('routines.isActive') : t('routines.inactive')} 
              color={routine.is_active ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>

        {/* Shared By Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon sx={{ fontSize: 16 }} />
            <Typography variant="subtitle2">
              {t('routines.sharedBy')} {shared_by_user.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {shared_by_user.email} • {formatDate(created_at)}
          </Typography>
          
          {message && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MessageIcon sx={{ fontSize: 16 }} />
                <Typography variant="subtitle2">
                  {t('routines.message')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{message}"
              </Typography>
            </Box>
          )}
        </Box>

        {/* Routine Days */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('routines.workoutDays')}
          </Typography>
          
          {routine.days.map((day, index) => (
            <Accordion key={day.id || index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1">
                    {day.day_name || `${t('routines.day')} ${day.day_number}`}
                  </Typography>
                  <Chip 
                    label={`${day.exercises.length} ${t('routines.exercises')}`} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                {day.notes && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {day.notes}
                  </Alert>
                )}
                
                <List dense>
                  {day.exercises.map((exercise, exerciseIndex) => (
                    <Box key={exercise.id || exerciseIndex}>
                      <ListItem>
                        <ListItemIcon>
                          <FitnessCenterIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={exercise.exercise?.name || 'Unknown Exercise'}
                          secondary={
                            <Box>
                              {exercise.sets.map((set, setIndex) => (
                                <Typography key={setIndex} variant="caption" display="block">
                                  {set.sets} sets × {set.reps} reps
                                  {set.notes && ` • ${set.notes}`}
                                </Typography>
                              ))}
                              {exercise.notes && (
                                <Typography variant="caption" color="text.secondary">
                                  {exercise.notes}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {exerciseIndex < day.exercises.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Accept Info */}
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('routines.acceptInfo')}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onDecline} 
          disabled={loading}
          startIcon={<CloseIcon />}
          color="error"
        >
          {t('routines.decline')}
        </Button>
        <Button onClick={onClose} disabled={loading}>
          {t('common.close')}
        </Button>
        <Button 
          onClick={onAccept} 
          variant="contained" 
          disabled={loading}
          startIcon={<CheckIcon />}
        >
          {t('routines.acceptRoutine')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoutinePreviewDialog;